<script setup>
import { reactive, computed, ref, getCurrentInstance } from 'vue';
import { useForm, router } from '@inertiajs/vue3';
import { useCurrencyFormatter } from '@/utils/currencyFormatter.js';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import { useLoading } from '@/composables/useLoading';
import { getCancellationTooltip, getStatusLabel, getStatusSeverity } from '@/composables/useOrderStatus.js';
import OrderStepProgress from '@/components/orders/OrderStepProgress.vue';
import ClientInformationSection from '../../../../components/invoice/ClientInformationSection.vue';

defineOptions({
    layout: AdminMainLayout,
});

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const { showLoading, hideLoading } = useLoading();

const props = defineProps({
    order: {
        type: Object,
        required: true
    },
    invoice: {
        type: Object,
        default: null
    }
});

const loading = ref(false);
const deleteOrderDialog = ref(false);

const goToEditOrder = () => router.visit(proxy.route ? proxy.route('orders.edit', props.order.id) : `/admin/orders/${props.order.id}/edit`);

const confirmForm = useForm({
    payment_date: props.order.payment_date ? new Date(props.order.payment_date) : null,
});

const cancelForm = useForm({});

const confirmPayment = () => {
    if (!confirmForm.payment_date) {
        alert('Por favor, selecione a data do pagamento.');
        return;
    }

    loading.value = true;

    const formattedDate = confirmForm.payment_date.toISOString().split('T')[0];

    confirmForm.put(
        proxy.route ? proxy.route('orders.confirm.deferred.payment', props.order.id) : `/admin/orders/${props.order.id}/confirm-deferred-payment`,
        {
            data: { payment_date: formattedDate },
            preserveScroll: true,
            onStart: () => showLoading($t('Confirming payment...')),
            onFinish: () => {
                hideLoading();
                loading.value = false;
            },
            onSuccess: () => {
                router.visit(
                    proxy.route ? proxy.route('orders.index') : '/admin/orders',
                    {
                        onStart: () => showLoading($t('Loading...')),
                        onFinish: () => hideLoading()
                    }
                );
            },
            onError: (errors) => {
                console.error('Payment confirmation failed:', errors);
            }
        }
    );
};

const showCancelDialog = () => {
    deleteOrderDialog.value = true;
};

const cancelOrder = () => {
    deleteOrderDialog.value = false;

    cancelForm.put(
        proxy.route ? proxy.route('orders.cancel', props.order.id) : `/admin/orders/${props.order.id}/cancel`,
        {
            preserveScroll: true,
            onStart: () => showLoading($t('Canceling order...')),
            onFinish: () => hideLoading(),
            onSuccess: () => {
                router.visit(
                    proxy.route ? proxy.route('orders.index') : '/admin/orders',
                    {
                        onStart: () => showLoading($t('Loading...')),
                        onFinish: () => hideLoading()
                    }
                );
            }
        }
    );
};

const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
};

const calculateTotal = () => {
    if (!props.order.order_items || props.order.order_items.length === 0) return 0;
    return props.order.order_items.reduce((total, item) => {
        return total + (item.sale || 0);
    }, 0);
};

const getOrderCurrency = () => {
    if (!props.order.order_items || props.order.order_items.length === 0) return 'USD';
    return props.order.order_items[0]?.sale_coin || 'USD';
};

const { formatCents } = useCurrencyFormatter();

const formatCurrency = (value, currency = 'USD') => {
    if (!value && value !== 0) return '-';
    return formatCents(value, currency);
};

const isPaymentConfirmed = computed(() => {
    const { payment_date, status } = props.order;

    if (payment_date) {
        return true;
    }

    return false;
});


const calculateVATAmount = () => {
    if (!props.invoice || !props.invoice.vat_rate || props.invoice.vat_rate <= 0) {
        return 0;
    }

    // VAT deve ser calculado sobre o subtotal real dos order items
    const subtotal = calculateOrderSubtotal();
    return (subtotal * props.invoice.vat_rate) / 100;
};

const calculateOrderSubtotal = () => {
    if (!props.order.order_items || props.order.order_items.length === 0) return 0;
    return props.order.order_items.reduce((total, item) => {
        return total + (item.sale || 0);
    }, 0);
};

const calculateInvoiceTotal = () => {
    if (!props.invoice) {
        return 0;
    }

    // Use o subtotal dos order items, não o invoice.value que pode já ter taxas incluídas
    const subtotal = calculateOrderSubtotal();
    let total = subtotal;

    // Adicionar payment tax apenas se existir
    if (props.invoice.payment_tax && props.invoice.payment_tax > 0) {
        total += parseFloat(props.invoice.payment_tax);
    }

    // Adicionar taxas dinâmicas
    if (props.invoice.invoice_taxes && props.invoice.invoice_taxes.length > 0) {
        props.invoice.invoice_taxes.forEach(tax => {
            total += calculateTaxDisplayValue(tax);
        });
    }

    // Adicionar VAT sobre o subtotal
    if (props.invoice.vat_rate && props.invoice.vat_rate > 0) {
        total += calculateVATAmount();
    }

    // Subtrair desconto
    if (props.invoice.discount && props.invoice.discount > 0) {
        total -= parseFloat(props.invoice.discount);
    }

    return Math.max(0, total);
};

const invoiceCurrency = computed(() => {
    return props.invoice?.value_coin || props.order?.order_items?.[0]?.sale_coin || 'USD';
});

const calculateTaxDisplayValue = (tax) => {
    if (!tax || !tax.value) return 0;

    if (tax.invoice_setting?.type === 'percentage') {
        // Para percentuais, usar o subtotal real dos order items
        const subtotal = calculateOrderSubtotal();
        return (subtotal * parseFloat(tax.value)) / 100;
    }

    if (tax.invoice_setting?.type === 'fixed_amount') {
        return parseFloat(tax.value) * 100;
    }

    return parseFloat(tax.value) * 100;
};

const getTaxDescription = (tax) => {
    if (!tax?.invoice_setting) {
        return `Taxa (${tax.value || '0'})`;
    }

    const setting = tax.invoice_setting;
    const name = setting.description || setting.name || setting.key || 'Taxa';

    if (setting.type === 'percentage') {
        return `${name} (${tax.value}%)`;
    }

    if (setting.type === 'fixed_amount') {
        return `${name} - Valor Fixo`;
    }

    return `${name}`;
};

const viewClientDetails = () => {
    if (props.order.client?.id) {
        router.visit(
            proxy.route ? proxy.route('clients.edit', props.order.client.id) : `/admin/clients/${props.order.client.id}/edit`
        );
    }
};

</script>

<template>
    <div class="grid pb-10">
        <!-- Header Card -->
        <Card class="mb-6">
            <template #header>
                <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between p-6 pb-0 gap-4">
                    <div class="flex items-center gap-4">
                        <div class="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                            <i class="pi pi-dollar text-green-700 text-xl"></i>
                        </div>
                        <div>
                            <h1 class="text-2xl font-bold text-surface-900">
                                {{ $t('Payment Confirmation') }}
                            </h1>
                            <p class="text-surface-600 mt-1">
                                {{ $t('Order') }} #{{ order.number }} - {{ order.client?.name || (order.client?.first_name + ' ' + order.client?.last_name) }}
                            </p>
                        </div>
                    </div>
                    <div class="flex items-center gap-3">
                        <Button
                            :label="$t('Edit order')"
                            icon="pi pi-pencil"
                            size="small"
                            outlined
                            @click="goToEditOrder"
                        />
                        <Button
                            :label="$t('Back to orders')"
                            icon="pi pi-arrow-left"
                            severity="secondary"
                            @click="() => {
                                router.visit(
                                    proxy.route ? proxy.route('orders.index') : '/admin/orders',
                                    {
                                        onStart: () => showLoading($t('Loading...')),
                                        onFinish: () => hideLoading()
                                    }
                                );
                            }"
                        />
                    </div>
                </div>
            </template>

            <template #content>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div class="p-4 bg-surface-50 rounded-lg border border-surface-200">
                        <div class="flex items-center gap-3">
                            <div class="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center">
                                <i class="pi pi-list text-white text-xl"></i>
                            </div>
                            <div>
                                <p class="text-sm text-surface-600">{{ $t('Total Items') }}</p>
                                <p class="text-2xl font-bold text-surface-900">{{ order.order_items?.length || 0 }}</p>
                            </div>
                        </div>
                    </div>

                    <div class="p-4 bg-surface-50 rounded-lg border border-surface-200">
                        <div class="flex items-center gap-3">
                            <div class="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                                <i class="pi pi-dollar text-white text-xl"></i>
                            </div>
                            <div>
                                <p class="text-sm text-surface-600">{{ $t('Total Amount') }}</p>
                                <p class="text-2xl font-bold text-surface-900">{{ formatCurrency(calculateInvoiceTotal(), invoiceCurrency) }}</p>
                            </div>
                        </div>
                    </div>

                    <div class="p-4 bg-surface-50 rounded-lg border border-surface-200">
                        <div class="flex items-center gap-3">
                            <div class="w-12 h-12 rounded-full" :class="isPaymentConfirmed ? 'bg-green-500' : 'bg-amber-500'">
                                <i class="text-white text-xl flex items-center justify-center h-full" :class="isPaymentConfirmed ? 'pi pi-check' : 'pi pi-clock'"></i>
                            </div>
                            <div>
                                <p class="text-sm text-surface-600">{{ $t('Payment Status') }}</p>
                                <p class="text-2xl font-bold text-surface-900">{{ isPaymentConfirmed ? $t('Confirmed') : $t('Pending') }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </Card>

        <!-- Client Information Card -->
        <ClientInformationSection
          :client="order.client"
          @view-client-details="viewClientDetails"
        />

        <!-- Order Summary Card -->
        <Card class="mb-6">
            <template #header>
                <div class="p-6 pb-0">
                    <div class="flex items-center gap-3">
                        <div class="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                            <i class="pi pi-shopping-cart text-purple-600 text-lg"></i>
                        </div>
                        <div>
                            <h2 class="text-xl font-bold text-surface-900">{{ $t('Order Summary') }}</h2>
                            <p class="text-sm text-surface-600">{{ order.order_items?.length || 0 }} {{ $t('items') }} - {{ formatDate(order.created_at) }}</p>
                        </div>
                    </div>
                </div>
            </template>
            <template #content>
                <div class="space-y-6">
                    <!-- Order Details -->
                    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div class="bg-gray-50 p-4 rounded-lg border">
                            <label class="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{{ $t('Order Number') }}</label>
                            <p class="text-lg font-semibold text-gray-900">#{{ order.number }}</p>
                        </div>
                        <div class="bg-gray-50 p-4 rounded-lg border">
                            <label class="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{{ $t('Status') }}</label>
                            <div class="flex items-center">
                                <Tag v-if="order.status === 'CANCELED'" :value="getStatusLabel(order.status, $t)" :severity="getStatusSeverity(order.status)" v-tooltip.top="getCancellationTooltip(order, $t)" />
                                <Tag v-else :value="getStatusLabel(order.status, $t)" :severity="getStatusSeverity(order.status)" />
                            </div>
                        </div>
                        <div class="bg-gray-50 p-4 rounded-lg border">
                            <label class="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{{ $t('Created At') }}</label>
                            <p class="text-lg font-semibold text-gray-900">{{ formatDate(order.created_at) }}</p>
                        </div>
                        <div class="bg-gray-50 p-4 rounded-lg border">
                            <label class="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{{ $t('Total Items') }}</label>
                            <p class="text-lg font-semibold text-gray-900">{{ order.order_items?.length || 0 }}</p>
                        </div>
                    </div>

                    <!-- Items Table -->
                    <div v-if="order.order_items && order.order_items.length > 0" class="border border-gray-200 rounded-lg overflow-hidden">
                        <div class="bg-gray-50 px-6 py-3 border-b border-gray-200">
                            <h3 class="text-sm font-medium text-gray-900">{{ $t('Order Items') }}</h3>
                        </div>
                        <DataTable :value="order.order_items" class="border-none payment-confirmation-table" scrollable scrollHeight="600px" responsiveLayout="scroll">
                            <Column :header="$t('Site')" class="text-sm" style="min-width:280px; width:35%" headerStyle="min-width:280px; width:35%">
                                <template #body="slotProps">
                                    <span class="text-gray-900 font-medium break-all">
                                        {{ slotProps.data.site?.url || slotProps.data.site?.name || '-' }}
                                    </span>
                                </template>
                            </Column>
                            <Column :header="$t('Type')" class="text-sm" style="min-width:120px; white-space:nowrap">
                                <template #body="slotProps">
                                    <span class="text-gray-600">
                                        {{ slotProps.data.type?.name || '-' }}
                                    </span>
                                </template>
                            </Column>
                            <Column :header="$t('Sale Value')" class="text-sm text-right" style="min-width:120px; white-space:nowrap">
                                <template #body="slotProps">
                                    <span class="font-semibold text-gray-900">
                                        {{ formatCurrency(slotProps.data.sale, slotProps.data.sale_coin) }}
                                    </span>
                                </template>
                            </Column>
                            <Column :header="$t('Observations')" class="text-sm" style="min-width:200px">
                                <template #body="slotProps">
                                    <span class="text-gray-600 text-xs">
                                        {{ slotProps.data.observations || '-' }}
                                    </span>
                                </template>
                            </Column>
                            <Column :header="$t('Actions')" class="text-sm" style="min-width:120px; white-space:nowrap">
                                <template #body="slotProps">
                                    <Tag v-if="slotProps.data.canceled_at" :value="$t('Canceled')" severity="danger" />
                                </template>
                            </Column>
                        </DataTable>
                    </div>
                </div>
            </template>
        </Card>

        <!-- Invoice Summary Card -->
        <Card v-if="invoice" class="mb-6">
            <template #header>
                <div class="p-6 pb-0">
                    <div class="flex items-center gap-3">
                        <div class="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                            <i class="pi pi-file-pdf text-orange-700 text-lg"></i>
                        </div>
                        <div>
                            <h2 class="text-xl font-bold text-surface-900">{{ $t('Invoice Details') }}</h2>
                            <p class="text-sm text-surface-600">{{ $t('Invoice') }} #{{ invoice.number || invoice.id }} - {{ formatDate(invoice.created_at) }}</p>
                        </div>
                    </div>
                </div>
            </template>
            <template #content>
                <div class="space-y-4">

                    <!-- Invoice Breakdown -->
                    <div class="mt-3">
                        <div class="bg-white rounded-lg border p-4">
                            <h3 class="text-lg font-semibold text-gray-900 mb-4">{{ $t('Invoice Values') }}</h3>

                            <div class="space-y-3">
                                <!-- Subtotal -->
                                <div class="flex justify-between items-center">
                                    <span class="text-gray-600">{{ $t('Subtotal') }}</span>
                                    <span class="font-medium text-gray-900">{{ formatCurrency(calculateOrderSubtotal(), invoiceCurrency) }}</span>
                                </div>

                                <!-- Payment Tax -->
                                <div v-if="invoice.payment_tax && invoice.payment_tax > 0" class="flex justify-between items-center">
                                    <span class="text-gray-600">{{ $t('Payment Tax') }}</span>
                                    <span class="font-medium text-gray-900">{{ formatCurrency(invoice.payment_tax, invoiceCurrency) }}</span>
                                </div>

                                <!-- Dynamic Taxes -->
                                <div v-if="invoice.invoice_taxes && invoice.invoice_taxes.length > 0">
                                    <div v-for="tax in invoice.invoice_taxes" :key="tax.id" class="flex justify-between items-center">
                                        <span class="text-gray-600">{{ getTaxDescription(tax) }}</span>
                                        <span class="font-medium text-gray-900">{{ formatCurrency(calculateTaxDisplayValue(tax), invoiceCurrency) }}</span>
                                    </div>
                                </div>

                                <!-- VAT -->
                                <div v-if="invoice.vat_rate && invoice.vat_rate > 0" class="flex justify-between items-center">
                                    <span class="text-gray-600">{{ $t('VAT') }} ({{ invoice.vat_rate }}%)</span>
                                    <span class="font-medium text-gray-900">{{ formatCurrency(calculateVATAmount(), invoiceCurrency) }}</span>
                                </div>

                                <!-- Discount -->
                                <div v-if="invoice.discount && invoice.discount > 0" class="flex justify-between items-center text-red-600">
                                    <span>{{ $t('Discount') }}</span>
                                    <span class="font-medium">-{{ formatCurrency(invoice.discount, invoiceCurrency) }}</span>
                                </div>

                                <!-- Total -->
                                <div class="flex justify-between items-center pt-3 border-t border-gray-200 mt-3">
                                    <span class="text-lg font-bold text-gray-900">{{ $t('Total') }}</span>
                                    <span class="text-xl font-bold text-blue-600">{{ formatCurrency(calculateInvoiceTotal(), invoiceCurrency) }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </Card>

        <!-- Payment Confirmation Card -->
        <Card class="mb-6">
            <template #header>
                <div class="p-6 pb-0">
                    <div class="flex items-center gap-3">
                        <div class="w-10 h-10 rounded-full flex items-center justify-center"
                            :class="isPaymentConfirmed ? 'bg-green-100' : 'bg-amber-100'">
                            <i class="text-lg"
                                :class="isPaymentConfirmed ? 'pi pi-check-circle text-green-700' : 'pi pi-credit-card text-amber-700'"></i>
                        </div>
                        <div>
                            <h2 class="text-xl font-bold text-surface-900">{{ $t('Payment Details') }}</h2>
                            <p class="text-sm" :class="isPaymentConfirmed ? 'text-green-600 font-medium' : 'text-amber-600 font-medium'">
                                {{ isPaymentConfirmed ? $t('Payment confirmed') : $t('Awaiting payment confirmation') }}
                            </p>
                        </div>
                    </div>
                </div>
            </template>
            <template #content>
                <div class="space-y-4">
                    <!-- Payment Status Message -->
                    <div v-if="isPaymentConfirmed" class="p-4 bg-green-50 rounded-lg border border-green-200">
                        <div class="flex items-center gap-2 text-green-700">
                            <i class="pi pi-check-circle text-xl"></i>
                            <p class="font-medium">{{ $t('Payment has already been confirmed for this order') }}</p>
                        </div>
                    </div>

                    <!-- Payment Date Input -->
                    <div class="p-4 bg-surface-50 rounded-lg border border-surface-200 flex justify-between items-center">
                      <div>
                          <label class="block text-sm font-medium text-surface-700 mb-3">
                              {{ isPaymentConfirmed ? $t('Payment Date') : $t('Select the Payment Date') }}
                              <span v-if="!isPaymentConfirmed" class="text-red-500">*</span>
                          </label>

                          <Calendar
                              v-model="confirmForm.payment_date"
                              showIcon
                              dateFormat="dd/mm/yy"
                              class="w-full md:w-[400px]"
                              :class="{ 'p-invalid': confirmForm.errors.payment_date }"
                              :readonly="isPaymentConfirmed"
                              :disabled="isPaymentConfirmed"
                          />

                          <small v-if="confirmForm.errors.payment_date" class="text-red-600 block mt-2">
                              {{ confirmForm.errors.payment_date }}
                          </small>
                      </div>

                      <div class="flex flex-col gap-3">
                          <label class="block text-sm font-medium mb-3 opacity-0">Button</label>
                          <Button
                              :label="$t('Confirm Payment')"
                              icon="pi pi-check"
                              severity="success"
                              @click="confirmPayment"
                              :loading="confirmForm.processing"
                              :disabled="isPaymentConfirmed || confirmForm.processing || cancelForm.processing || !confirmForm.payment_date"
                          />
                      </div>
                  </div>

                </div>
            </template>
        </Card>
    </div>
</template>

<style scoped>
/* Payment confirmation items table: header titles must always stay on a single line */
.payment-confirmation-table :deep(.p-datatable-column-title) {
    white-space: nowrap;
}

.payment-confirmation-table :deep(.p-datatable-header-cell) {
    white-space: nowrap;
}
</style>
